// src/tattoo/components/ArtistWizardModal.jsx
import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";

// Common tattoo styles – tweak anytime
const COMMON_STYLES = [
  "Traditional",
  "Neo-traditional",
  "Black & Grey",
  "Fine line",
  "Japanese",
  "Realism",
  "Illustrative",
  "Ornamental",
  "Lettering",
  "Geometric",
];

function toggleItem(list, value) {
  if (list.includes(value)) {
    return list.filter((v) => v !== value);
  }
  return [...list, value];
}

export default function ArtistWizardModal({
  open,
  onClose,
  onSubmitProfile,
  initialProfile,
}) {
  const [step, setStep] = useState(1);

  // core form state (seeded from initialProfile when present)
  const [role, setRole] = useState(initialProfile?.role || "artist");
  const [displayName, setDisplayName] = useState(
    initialProfile?.displayName || initialProfile?.studioName || ""
  );
  const [studioName, setStudioName] = useState(
    initialProfile?.studioName || ""
  );
  const [tagline, setTagline] = useState(initialProfile?.tagline || "");
  const [bio, setBio] = useState(initialProfile?.bio || "");

  const [styleTags, setStyleTags] = useState(
    initialProfile?.styleTags || initialProfile?.styles || []
  );
  const [styleNotes, setStyleNotes] = useState(
    initialProfile?.styleNotes || ""
  );
  const [street, setStreet] = useState(initialProfile?.street || "");
  const [postalCode, setPostalCode] = useState(
    initialProfile?.postalCode || ""
  );
  const [city, setCity] = useState(initialProfile?.city || "");
  const [region, setRegion] = useState(initialProfile?.region || "");
  const [country, setCountry] = useState(initialProfile?.country || "");
  const [website, setWebsite] = useState(initialProfile?.website || "");
  const [instagram, setInstagram] = useState(initialProfile?.instagram || "");
  const [acceptPoolRequests, setAcceptPoolRequests] = useState(
    initialProfile?.acceptPoolRequests ?? true
  );
  // 'instant'    — email per pool request (current default)
  // 'inbox_only' — only see pool requests in the in-app inbox + badge
  const [poolNotificationMode, setPoolNotificationMode] = useState(
    initialProfile?.poolNotificationMode || "instant"
  );

  const [contactEmail, setContactEmail] = useState(
    initialProfile?.contactEmail || ""
  );

  // Public studio phone (free-form string) and hours (free-form string for
  // v1 — the public page's parseHours accepts string, array, or object so
  // a structured editor can ship later without breaking existing data).
  const [phone, setPhone] = useState(initialProfile?.phone || "");
  const [hours, setHours] = useState(
    typeof initialProfile?.hours === "string"
      ? initialProfile.hours
      : initialProfile?.hours
      ? JSON.stringify(initialProfile.hours)
      : ""
  );

  // reset / hydrate when the modal opens
  useEffect(() => {
    if (!open) return;
    setStep(1);

    if (initialProfile) {
      setRole(initialProfile.role || "artist");
      setDisplayName(
        initialProfile.displayName || initialProfile.studioName || ""
      );
      setStudioName(initialProfile.studioName || "");
      setTagline(initialProfile.tagline || "");
      setBio(initialProfile.bio || "");

      setStyleTags(initialProfile.styleTags || initialProfile.styles || []);
      setStyleNotes(initialProfile.styleNotes || "");

      setCity(initialProfile.city || "");
      setRegion(initialProfile.region || "");
      setCountry(initialProfile.country || "");
      setWebsite(initialProfile.website || "");
      setInstagram(initialProfile.instagram || "");
      setAcceptPoolRequests(initialProfile.acceptPoolRequests ?? true);
      setPoolNotificationMode(initialProfile.poolNotificationMode || "instant");
      setContactEmail(initialProfile.contactEmail || "");
      setStreet(initialProfile.street || "");
      setPostalCode(initialProfile.postalCode || "");
      setPhone(initialProfile.phone || "");
      setHours(
        typeof initialProfile.hours === "string"
          ? initialProfile.hours
          : initialProfile.hours
          ? JSON.stringify(initialProfile.hours)
          : ""
      );
    }
  }, [open, initialProfile]);

  if (!open) return null;

  const canNextFrom1 = !!displayName.trim();
  const canNextFrom2 = styleTags.length > 0 || !!styleNotes.trim();
  const canSubmit =
    !!displayName.trim() && !!city.trim() && !!contactEmail.trim();

  const profilePayload = {
    role,
    displayName: displayName.trim(),
    studioName: studioName.trim() || null,
    tagline: tagline.trim() || null,
    bio: bio.trim() || null,
    styleTags,
    styleNotes: styleNotes.trim() || null,
    city: city.trim() || null,
    region: region.trim() || null,
    country: country.trim() || null,
    website: website.trim() || null,
    instagram: instagram.trim() || null,
    contactEmail: contactEmail.trim(),
    acceptPoolRequests,
    // Only meaningful when acceptPoolRequests is true, but we keep the user's
    // last choice in state regardless so re-opting-in doesn't reset it.
    poolNotificationMode,
    street: street.trim() || null,
    postalCode: postalCode.trim() || null,
    phone: phone.trim() || null,
    // For v1 we capture hours as a free-form string. The public page's
    // parseHours helper renders strings as a single line. If we later
    // ship a structured day-by-day editor, the column accepts jsonb so
    // nothing migrates.
    hours: hours.trim() || null,
  };

  async function handleSubmit() {
    try {
      if (onSubmitProfile) {
        await onSubmitProfile(profilePayload);
      } else {
        console.log("[wizard] profile submit", profilePayload);
      }
      onClose?.();
    } catch (err) {
      console.error("[wizard] submit error", err);
      toast.error("Something went wrong saving your profile.");
    }
  }

  const StepDots = () => (
    <div className="tt-wizard-steps">
      {[1, 2, 3, 4].map((n) => (
        <span
          key={n}
          className={`tt-wizard-step-dot ${step === n ? "is-active" : ""}`}
        />
      ))}
    </div>
  );

  const modalContent = (
    <div className="tt-modal-backdrop">
      <div className="tt-modal tt-wizard-modal">
        {/* Header */}
        <div className="tt-wizard-header">
          <div>
            <h2 className="tt-wizard-title">Become a Tattune artist</h2>
            <p className="tt-wizard-subtitle">
              Tell us about your studio or tattoo practice so clients can find
              you, and you can opt into the quote pool.
            </p>
          </div>
          <button
            type="button"
            className="tt-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <StepDots />

        {/* Body */}
        <div className="tt-wizard-body">
          {step === 1 && (
            <Step1Identity
              role={role}
              setRole={setRole}
              displayName={displayName}
              setDisplayName={setDisplayName}
              studioName={studioName}
              setStudioName={setStudioName}
              tagline={tagline}
              setTagline={setTagline}
              bio={bio}
              setBio={setBio}
            />
          )}

          {step === 2 && (
            <Step2Styles
              styleTags={styleTags}
              setStyleTags={setStyleTags}
              styleNotes={styleNotes}
              setStyleNotes={setStyleNotes}
            />
          )}

          {step === 3 && (
            <Step3Location
              city={city}
              setCity={setCity}
              region={region}
              setRegion={setRegion}
              country={country}
              setCountry={setCountry}
              website={website}
              setWebsite={setWebsite}
              instagram={instagram}
              setInstagram={setInstagram}
              acceptPoolRequests={acceptPoolRequests}
              setAcceptPoolRequests={setAcceptPoolRequests}
              poolNotificationMode={poolNotificationMode}
              setPoolNotificationMode={setPoolNotificationMode}
              contactEmail={contactEmail}
              setContactEmail={setContactEmail}
              street={street}
              setStreet={setStreet}
              postalCode={postalCode}
              setPostalCode={setPostalCode}
              phone={phone}
              setPhone={setPhone}
              hours={hours}
              setHours={setHours}
            />
          )}

          {step === 4 && <Step4Review profile={profilePayload} />}
        </div>

        {/* Footer / controls */}
        <div className="tt-wizard-footer">
          <div className="tt-wizard-footer-left">
            {step > 1 ? (
              <button
                type="button"
                className="tt-btn"
                onClick={() => setStep((s) => Math.max(1, s - 1))}
              >
                ← Back
              </button>
            ) : (
              <span className="tt-wizard-hint">
                You can edit this later from the left panel.
              </span>
            )}
          </div>

          <div className="tt-wizard-footer-right">
            {step < 4 && (
              <button
                type="button"
                className="tt-btn tt-btn--fill-primary"
                disabled={
                  (step === 1 && !canNextFrom1) || (step === 2 && !canNextFrom2)
                }
                onClick={() => setStep((s) => Math.min(4, s + 1))}
              >
                Next →
              </button>
            )}

            {step === 4 && (
              <button
                type="button"
                className="tt-btn tt-btn--fill-primary"
                disabled={!canSubmit}
                onClick={handleSubmit}
              >
                Save profile
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Render via portal so it’s *outside* the left panel and full-screen on phone
  if (typeof document === "undefined") {
    return modalContent; // SSR safety
  }
  return createPortal(modalContent, document.body);
}

/* ---------- Step 1: identity ---------- */

function Step1Identity({
  role,
  setRole,
  displayName,
  setDisplayName,
  studioName,
  setStudioName,
  tagline,
  setTagline,
  bio,
  setBio,
}) {
  return (
    <div className="tt-wizard-section">
      <h3 className="tt-wizard-step-title">Who are you?</h3>
      <p className="tt-wizard-step-copy">
        This is what clients and fellow artists will see in the directory and
        quote pool.
      </p>

      <div className="tt-wizard-field">
        <label className="tt-wizard-label">Public display name *</label>
        <input
          className="tt-wizard-input"
          placeholder="Dark Tides · Mina Ortega · Studio Nine"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
        />
      </div>

      <div className="tt-wizard-field">
        <label className="tt-wizard-label">
          Studio name (optional, if different)
        </label>
        <input
          className="tt-wizard-input"
          placeholder="Studio or shop name"
          value={studioName}
          onChange={(e) => setStudioName(e.target.value)}
        />
      </div>

      <div className="tt-wizard-field">
        <label className="tt-wizard-label">Tagline</label>
        <input
          className="tt-wizard-input"
          placeholder="Illustrative blackwork · fine line florals · custom only"
          value={tagline}
          onChange={(e) => setTagline(e.target.value)}
        />
      </div>

      <div className="tt-wizard-field">
        <label className="tt-wizard-label">About you</label>
        <textarea
          className="tt-wizard-textarea"
          placeholder="Tell clients what you love to tattoo, your approach, or what makes your studio different."
          value={bio}
          onChange={(e) => setBio(e.target.value)}
        />
      </div>

      <div className="tt-wizard-field">
        <label className="tt-wizard-label">How you use Tattune</label>
        <div className="tt-wizard-pill-row">
          <button
            type="button"
            className={`tt-wizard-pill ${role === "artist" ? "is-on" : ""}`}
            onClick={() => setRole("artist")}
          >
            I’m an artist
          </button>
          <button
            type="button"
            className={`tt-wizard-pill ${
              role === "studio+artist" ? "is-on" : ""
            }`}
            onClick={() => setRole("studio+artist")}
          >
            I run a studio & tattoo
          </button>
          <button
            type="button"
            className={`tt-wizard-pill ${role === "studio" ? "is-on" : ""}`}
            onClick={() => setRole("studio")}
          >
            I run a studio (no tattooing)
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Step 2: styles ---------- */

function Step2Styles({ styleTags, setStyleTags, styleNotes, setStyleNotes }) {
  return (
    <div className="tt-wizard-section">
      <h3 className="tt-wizard-step-title">What do you specialize in?</h3>
      <p className="tt-wizard-step-copy">
        Pick the styles you love to tattoo most. This helps clients find the
        right fit.
      </p>

      <div className="tt-wizard-field">
        <label className="tt-wizard-label">Styles</label>
        <div className="tt-wizard-pill-row tt-wizard-pill-row-wrap">
          {COMMON_STYLES.map((style) => {
            const on = styleTags.includes(style);
            return (
              <button
                key={style}
                type="button"
                className={`tt-wizard-pill ${on ? "is-on" : ""}`}
                onClick={() => setStyleTags((prev) => toggleItem(prev, style))}
              >
                {style}
              </button>
            );
          })}
        </div>
      </div>

      <div className="tt-wizard-field">
        <label className="tt-wizard-label">Anything else? (optional)</label>
        <textarea
          className="tt-wizard-textarea"
          placeholder="Cover-ups, scar work, large-scale projects only, no finger tattoos, etc."
          value={styleNotes}
          onChange={(e) => setStyleNotes(e.target.value)}
        />
      </div>
    </div>
  );
}

/* ---------- Step 3: location & links ---------- */

function Step3Location({
  city,
  setCity,
  region,
  setRegion,
  country,
  setCountry,
  website,
  setWebsite,
  instagram,
  setInstagram,
  acceptPoolRequests,
  setAcceptPoolRequests,
  poolNotificationMode,
  setPoolNotificationMode,
  contactEmail,
  setContactEmail,
  street,
  setStreet,
  postalCode,
  setPostalCode,
  phone,
  setPhone,
  hours,
  setHours,
}) {
  return (
    <div className="tt-wizard-section">
      <h3 className="tt-wizard-step-title">Where can people find you?</h3>
      <p className="tt-wizard-step-copy">
        We use this for the map links and to route quote requests from nearby
        clients.
      </p>

      {/* Contact email + phone */}
      <div className="tt-wizard-grid-2">
        <div className="tt-wizard-field">
          <label className="tt-wizard-label">Contact email *</label>
          <input
            className="tt-wizard-input"
            type="email"
            placeholder="you@yourstudio.com"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
          />
        </div>
        <div className="tt-wizard-field">
          <label className="tt-wizard-label">Phone</label>
          <input
            className="tt-wizard-input"
            type="tel"
            placeholder="+1 613-555-0199"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>
      </div>

      {/* Studio hours — free-form for v1. Renders verbatim on the public
          studio page; structured day-by-day editor can replace this later
          (the column is jsonb and parseHours accepts strings, arrays,
          or objects). */}
      <div className="tt-wizard-field">
        <label className="tt-wizard-label">Hours</label>
        <textarea
          className="tt-wizard-input"
          placeholder={"Mon–Fri 11am–7pm\nSat 12pm–6pm\nSun closed"}
          rows={3}
          value={hours}
          onChange={(e) => setHours(e.target.value)}
          style={{ fontFamily: "inherit", lineHeight: 1.4, resize: "vertical" }}
        />
        <p className="tt-wizard-small">
          Free-form — write whatever reads cleanly on your studio page.
        </p>
      </div>

      <div className="tt-wizard-grid-2">
        <div className="tt-wizard-field">
          <label className="tt-wizard-label">City *</label>
          <input
            className="tt-wizard-input"
            placeholder="Toronto"
            value={city}
            onChange={(e) => setCity(e.target.value)}
          />
        </div>
        <div className="tt-wizard-field">
        <label className="tt-wizard-label">Street address</label>
        <input
          className="tt-wizard-input"
          placeholder="1 Place D'armes unit 45"
          value={street}
          onChange={(e) => setStreet(e.target.value)}
        />
      </div>

      <div className="tt-wizard-field">
        <label className="tt-wizard-label">Postal / ZIP code</label>
        <input
          className="tt-wizard-input"
          placeholder="K7K 3X4"
          value={postalCode}
          onChange={(e) => setPostalCode(e.target.value)}
        />
      </div>
        <div className="tt-wizard-field">
          <label className="tt-wizard-label">Region / State / Province</label>
          <input
            className="tt-wizard-input"
            placeholder="ON · CA · NSW · etc."
            value={region}
            onChange={(e) => setRegion(e.target.value)}
          />
        </div>
      </div>

      <div className="tt-wizard-field">
        <label className="tt-wizard-label">Country</label>
        <input
          className="tt-wizard-input"
          placeholder="Canada"
          value={country}
          onChange={(e) => setCountry(e.target.value)}
        />
      </div>

      <div className="tt-wizard-grid-2">
        <div className="tt-wizard-field">
          <label className="tt-wizard-label">Website</label>
          <input
            className="tt-wizard-input"
            placeholder="https://yourstudio.com"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
        </div>
        <div className="tt-wizard-field">
          <label className="tt-wizard-label">Instagram</label>
          <input
            className="tt-wizard-input"
            placeholder="https://instagram.com/yourhandle"
            value={instagram}
            onChange={(e) => setInstagram(e.target.value)}
          />
        </div>
      </div>

      <div className="tt-wizard-field">
        <label className="tt-wizard-label">Quote pool</label>
        <div className="tt-wizard-pool-row">
          <button
            type="button"
            className={`tt-wizard-pill ${acceptPoolRequests ? "is-on" : ""}`}
            onClick={() => setAcceptPoolRequests(true)}
          >
            Opt in – send me relevant quote requests
          </button>
          <button
            type="button"
            className={`tt-wizard-pill ${!acceptPoolRequests ? "is-on" : ""}`}
            onClick={() => setAcceptPoolRequests(false)}
          >
            Not yet
          </button>
        </div>
        <p className="tt-wizard-small">
          When clients post a design to the open quote pool, you'll see them
          in your inbox if they match your style and location. You can
          always change this later.
        </p>

        {/* Sub-toggle: only meaningful when opted in. We keep the user's
            choice in state regardless so re-opting-in restores their last
            preference. */}
        {acceptPoolRequests && (
          <div
            className="tt-wizard-field"
            style={{ marginTop: 14, paddingLeft: 4 }}
          >
            <label className="tt-wizard-label">
              How should we notify you?
            </label>
            <div className="tt-wizard-pool-row">
              <button
                type="button"
                className={`tt-wizard-pill ${
                  poolNotificationMode === "instant" ? "is-on" : ""
                }`}
                onClick={() => setPoolNotificationMode("instant")}
              >
                Email me each one
              </button>
              <button
                type="button"
                className={`tt-wizard-pill ${
                  poolNotificationMode === "inbox_only" ? "is-on" : ""
                }`}
                onClick={() => setPoolNotificationMode("inbox_only")}
              >
                Inbox only — no emails
              </button>
            </div>
            <p className="tt-wizard-small">
              {poolNotificationMode === "inbox_only"
                ? "We won't send you a pool email — they'll just appear in your inbox with an unread badge."
                : "We'll email you the moment a matching pool request comes in."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- Step 4: review ---------- */

function Step4Review({ profile }) {
  const {
    displayName,
    studioName,
    tagline,
    bio,
    styleTags,
    styleNotes,
    city,
    region,
    country,
    website,
    instagram,
    acceptPoolRequests,
    poolNotificationMode,
    role,
  } = profile;

  const loc = [city, region, country].filter(Boolean).join(", ");

  return (
    <div className="tt-wizard-section">
      <h3 className="tt-wizard-step-title">Review</h3>
      <p className="tt-wizard-step-copy">
        Here’s how your profile will look in the directory and quote pool.
      </p>

      <div className="tt-wizard-review-card">
        <div className="tt-wizard-review-header">
          <div className="tt-wizard-avatar">
            {displayName
              .split(/\s+/)
              .slice(0, 2)
              .map((p) => p[0])
              .join("")
              .toUpperCase()}
          </div>
          <div className="tt-wizard-review-main">
            <div className="tt-wizard-review-name">{displayName}</div>
            {studioName && studioName !== displayName && (
              <div className="tt-wizard-review-sub">{studioName}</div>
            )}
            {tagline && (
              <div className="tt-wizard-review-tagline">{tagline}</div>
            )}
            {loc && <div className="tt-wizard-review-meta">{loc}</div>}
          </div>
        </div>

        {bio && (
          <div className="tt-wizard-review-block">
            <div className="tt-wizard-review-label">About</div>
            <p>{bio}</p>
          </div>
        )}

        {(styleTags?.length || styleNotes) && (
          <div className="tt-wizard-review-block">
            <div className="tt-wizard-review-label">Styles</div>
            {styleTags?.length > 0 && (
              <div className="tt-artist-tags" style={{ marginBottom: 6 }}>
                {styleTags.map((s) => (
                  <span key={s} className="tt-artist-tag">
                    {s}
                  </span>
                ))}
              </div>
            )}
            {styleNotes && <p>{styleNotes}</p>}
          </div>
        )}

        {(website || instagram) && (
          <div className="tt-wizard-review-block">
            <div className="tt-wizard-review-label">Links</div>
            <div className="tt-artist-links">
              {website && <span className="tt-artist-link">🌐 Website</span>}
              {instagram && (
                <span className="tt-artist-link">📸 Instagram</span>
              )}
            </div>
          </div>
        )}

        <div className="tt-wizard-review-block">
          <div className="tt-wizard-review-label">Quote pool</div>
          <p>
            {role === "artist" && "You’ll appear as an individual artist."}
            {role === "studio+artist" &&
              "You’ll appear as a studio where you also tattoo."}
            {role === "studio" &&
              "You’ll appear as a studio that brings artists in."}{" "}
            {acceptPoolRequests
              ? poolNotificationMode === "inbox_only"
                ? "You’re opted in — pool requests show up in your inbox (no emails)."
                : "You’re opted in — we’ll email you when a matching pool request comes in."
              : "You’re currently not receiving quote requests from the pool."}
          </p>
        </div>

        {/* Next step pointer — gallery management is post-onboarding and
            lives at its own URL so it doesn't bloat the wizard. */}
        <div className="tt-wizard-review-block">
          <div className="tt-wizard-review-label">Studio profile</div>
          <p style={{ opacity: 0.85 }}>
            Upload a logo, recent-work photos, and your flash gallery —
            all the imagery that shows on your public artist page.{" "}
            <a
              href="/account/studio"
              style={{ color: "#f6caca", textDecoration: "underline" }}
            >
              Manage studio profile →
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
